<template>
  <div class="file-upload">
    <Upload
      :key="uploadKey"
      :files="model"
      :defaultFiles="[]"
      :batch="false"
      :multiple="true"
      :with-credentials="false"
      :autoUpload="false"
      :restrictions="{
        maxFileSize: maxTotalUploadSize,
        allowedExtensions: allowedAttachmentTypes,
      }"
      @add="onAdd"
      @remove="onRemove"
      @progress="onProgress"
      @statuschange="onStatusChange"
      class="file-upload__container"
      data-cy="upload-container"
    >
    </Upload>
    <p
      class="file-upload__types-label"
      v-if="allowedAttachmentTypes.length > 0"
    >
      Acceptable File Types: {{ allowedAttachmentTypes.join(", ") }}
    </p>
  </div>
</template>

<script setup lang="ts">
import {
  getAllowedAttachmentTypesAsync,
  getMaxTotalUploadSizeAsync,
} from "@/shared/helpers/fileHelper";
import { useMainStore } from "@/stores/useMainStore";
import {
  Upload,
  type UploadFileInfo,
  type UploadOnAddEvent,
  type UploadOnProgressEvent,
  type UploadOnRemoveEvent,
  type UploadOnStatusChangeEvent,
} from "@progress/kendo-vue-upload";
import { onBeforeMount, ref, watchEffect } from "vue";

const main = useMainStore();

const uploadKey = ref(0);
const model = defineModel<UploadFileInfo[]>({ default: [] });
const uploadState = ref<UploadFileInfo[]>(model.value);
const allowedAttachmentTypes = ref<string[]>([]);
const maxTotalUploadSize = ref<number>(25 * 1024 * 1024);

const onAdd = function (...args: any[]) {
  const event = args[0] as UploadOnAddEvent;
  uploadState.value = event.newState;
  model.value = event.newState;
};

const onRemove = function (...args: any[]) {
  const event = args[0] as UploadOnRemoveEvent;
  uploadState.value = event.newState;
  model.value = event.newState;
  return;
};

const onProgress = function (...args: any[]) {
  const event = args[0] as UploadOnProgressEvent;
  uploadState.value = event.newState;
  model.value = event.newState;
  return;
};

const onStatusChange = function (...args: any[]) {
  const event = args[0] as UploadOnStatusChangeEvent;
  uploadState.value = event.newState;
  model.value = event.newState;
  return;
};

watchEffect(() => {
  // Sync state with model and force re-render
  if (uploadState.value !== model.value) {
    uploadState.value = model.value;
    uploadKey.value++;
  }
});

onBeforeMount(async () => {
  allowedAttachmentTypes.value = await getAllowedAttachmentTypesAsync();
  maxTotalUploadSize.value = await getMaxTotalUploadSizeAsync();
});
</script>

<style lang="scss">
.file-upload {
  &__container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__types-label {
    display: inline-block;
    font-size: 12px;
    color: #999;
    font-weight: bold;
  }
}
</style>
